import { Component, computed, effect, ElementRef, forwardRef, Inject, input, OnInit, Optional, output, signal, viewChildren, ChangeDetectionStrategy } from '@angular/core';
import { LayoutType } from 'src/app/model/enums/layout-type.enum';
import { SlideDTO } from 'src/app/model/slideDTO.model';
import { EXPORT_PDF_MODE, IS_BRANCHING_SLIDE } from '../../constants/injection-tokens';
import { BaseSlideWidgetComponent } from '../base-slide-widget/base-slide-widget';
import { SlideBuilderSectionComponent } from '../slide-builder-section/slide-builder-section.component';


@Component({
  selector: 'app-slide-builder',
  templateUrl: './slide-builder.component.html',
  styleUrls: ['./slide-builder.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
  providers: [
    {
      provide: IS_BRANCHING_SLIDE,
      useFactory: (component: SlideBuilderComponent) => component.isBranchingSlide,
      deps: [forwardRef(() => SlideBuilderComponent)]
    }
  ]
})
export class SlideBuilderComponent implements OnInit {
  public slideBuilderSections = viewChildren(SlideBuilderSectionComponent);

  public slideData = input<SlideDTO>();
  public requiredToWatchVideo = input<boolean>();
  public requiredToCompleteInteraction = input<boolean>();
  public chapterContainerHeight = input<number>();

  public videoWatched = output<SlideDTO>();
  public answerChanged = output<{ slide: SlideDTO, answer: any }>();
  public interactionCompleted = output<{ slide: SlideDTO, completionRatio: number }>();

  public containerHeight = signal(0);
  public noGap = signal(false);

  public isBranchingSlide = computed(() => {
    const value = this.slideData();
  
    if (!value) {
      return false;
    }

    return !Array.isArray(value.branchingInfo?.branching) && !!value.branchingInfo?.branching?.sourceId
  });


  public readonly layoutTypes: typeof LayoutType = LayoutType;

  constructor(
    @Inject(EXPORT_PDF_MODE) @Optional() public exportMode: boolean,
    private readonly elementRef: ElementRef) {
      effect(() => {
        const data = this.slideData();
        this.setSlideBuilderCssVariables(data);
      })
    }

  ngOnInit() {
    this.containerHeight.set(this.elementRef.nativeElement.offsetHeight);
    this.addResizeObverveForContainer();
  }

  public getSlideWidgets(): Array<BaseSlideWidgetComponent> {
    return this.slideBuilderSections()?.reduce((total, section) => {
      return [...total, ...section.baseSlides(), ...section.questionBaseSlides()]
    }, []);
  }

  public slideAnswerChanged(data: { slide: SlideDTO, answer: any }): void {
    this.answerChanged.emit(data);
  }

  public slideInteractionCompleted(data: { slide: SlideDTO, completionRatio: number }): void {
    this.interactionCompleted.emit(data);
  }

  public emitVideoWatched(slide: SlideDTO): void {
    this.videoWatched.emit(slide);
  }

  private addResizeObverveForContainer(): void {
    if (!this.elementRef.nativeElement) {
      return;
    }

    const observer = new ResizeObserver(() => {
      this.containerHeight.set(this.elementRef.nativeElement.offsetHeight);
    });
    
    observer.observe(this.elementRef.nativeElement);
  }

  private setSlideBuilderCssVariables(data?: SlideDTO): void {
    this.elementRef.nativeElement.style.setProperty('--lms-slide-builder-gap', data?.gapBetweenRows ?? '');
    this.noGap.set(!data?.gapBetweenRows  || parseFloat(data?.gapBetweenRows) === 0);
  }

}
